import { FC, useMemo } from "react";
import { RouteComponentProps } from "react-router-dom";
import { isEmpty } from "lodash";
import __ from "src/utils/Translations";
import useBasePath from "src/utils/hooks/useBasePath";
import { useCompanyId } from "src/Pages/Company/CompanyContext";
import { absencesAbsencesConfigurationWrite } from "src/utils/RoleBasedSecurity/permissions";
import { usePermission } from "src/utils/hooks/permissions/usePermissions";
import { LogLevel, useLogStatus } from "src/utils/newRelic/useLogStatus";
import BreadcrumbsMui from "src/Components/BreadcrumbsMui/BreadcrumbsMui";
import Title from "src/Pages/Company/Absences/Title/Title";
import {
  useFetchCalendars,
  useFetchCalendar,
} from "src/Pages/Company/Absences/api/calendars.queries";
import { Loader } from "src/Pages/Company/Absences/Loader/Loader";
import ErrorDataFetching from "src/Components/Error/ErrorDataFetching";
import CalendarForm from "src/Pages/Company/Absences/Calendars/Calendar/CalendarForm";
import PermissionsAlert from "src/Pages/Company/Absences/PermissionsAlert";

interface IMatchParams {
  calendarIdToClone: string;
}

const CalendarClone: FC<RouteComponentProps<IMatchParams>> = ({ match }) => {
  const { calendarIdToClone } = match.params;
  const companyId = useCompanyId();
  const basePath = useBasePath();
  const canWriteAbsencesConfiguration = usePermission(
    absencesAbsencesConfigurationWrite
  );
  const logStatus = useLogStatus();

  if (!companyId) {
    logStatus({
      level: LogLevel.DEBUG,
      message: "No company ID provided.",
      file: "CalendarClone.tsx",
    });
  }

  if (!calendarIdToClone) {
    logStatus({
      level: LogLevel.DEBUG,
      message: "No calenndar ID to clone provided.",
      file: "CalendarClone.tsx",
    });
  }

  const {
    data: calendars,
    isFetching: isFetchingCalendars,
    isError: isErrorCalendars,
  } = useFetchCalendars(companyId);

  const {
    data: calendarData,
    isFetching,
    isSuccess,
    isError,
  } = useFetchCalendar(calendarIdToClone);

  const calendarNames = useMemo(() => {
    if (isEmpty(calendars)) return [];

    return calendars.map((calendar) => calendar.name);
  }, [calendars]);

  return (
    <>
      <BreadcrumbsMui
        items={[
          {
            label: __("Nieobecności"),
            to: `${basePath}/absences`,
          },
          {
            label: __("Lista kalendarzy"),
            to: `${basePath}/absences/calendars`,
          },
          {
            label: __("Dodawanie nowego kalendarza"),
          },
        ]}
      />
      <Title
        title={__("Dodawanie nowego kalendarza")}
        subtitle={__(
          "Przestrzeń do tworzenia i zarządzania kalendarzem rocznym."
        )}
      />
      {!canWriteAbsencesConfiguration && <PermissionsAlert />}
      {(isFetching || isFetchingCalendars) && <Loader />}
      {(isError || isErrorCalendars) && <ErrorDataFetching />}
      {isSuccess && (
        <CalendarForm
          calendarData={calendarData}
          calendarNames={calendarNames}
        />
      )}
    </>
  );
};

export default CalendarClone;
